import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrafficBadge, EstadoOrden } from "@/components/Atoms";
import { ChartLineUp, ListChecks, Warning, Buildings, UserList, Calendar, Clock, ShieldWarning, ShieldCheck } from "@phosphor-icons/react";

function TechnicianHseBanner({ cedula }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!cedula) return;
        api.get(`/technicians/${cedula}/documents`).then(r => setData(r.data)).catch(() => setData(null));
    }, [cedula]);
    if (!data) return null;
    const docs = data.documents || [];
    // Determinar cuáles críticos faltan o están vencidos
    const criticosOk = docs.filter(d => d.critico && d.status !== "rojo" && !d.reemplazado).map(d => d.tipo);
    const criticosVencidos = docs.filter(d => d.critico && d.status === "rojo" && !d.reemplazado);
    const necesarios = ["eps", "arl", "alturas", "examen_medico", "sst_50h"];
    const faltantes = necesarios.filter(n => !criticosOk.includes(n));
    const bloqueado = faltantes.length > 0 || criticosVencidos.length > 0;
    return (
        <div className={`mb-6 border p-5 flex items-start gap-4 ${bloqueado ? "border-red-400 bg-red-50" : "border-emerald-400 bg-emerald-50"}`} data-testid="tec-hse-banner">
            {bloqueado ? (
                <ShieldWarning size={32} weight="fill" style={{ color: "#DC2626" }} />
            ) : (
                <ShieldCheck size={32} weight="fill" style={{ color: "#059669" }} />
            )}
            <div className="flex-1">
                <div className="overline mb-1" style={{ color: bloqueado ? "#991B1B" : "#065F46" }}>
                    {bloqueado ? "🚫 Estás bloqueado para recibir órdenes" : "✅ Expediente HSE al día"}
                </div>
                <div className="font-display text-lg font-bold tracking-tight">
                    {bloqueado
                        ? "Sube o renueva estos documentos para volver a recibir órdenes:"
                        : "Todos tus documentos HSE críticos están vigentes."}
                </div>
                {bloqueado && (
                    <ul className="text-sm mt-2 space-y-0.5" style={{ color: "#7F1D1D" }}>
                        {faltantes.map(f => <li key={f}>• <span className="font-mono uppercase">{f}</span> — sin cargar</li>)}
                        {criticosVencidos.map(d => <li key={d.doc_id}>• {d.label} — <span className="font-mono">VENCIDO ({d.fecha_vencimiento})</span></li>)}
                    </ul>
                )}
                <Link to={`/technicians/${cedula}/documents`} className="inline-block mt-3 text-xs font-bold uppercase tracking-widest border-b-2 border-current" data-testid="link-mi-hse" style={{ color: bloqueado ? "#991B1B" : "#065F46" }}>
                    Ir a mi expediente HSE →
                </Link>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [myOrders, setMyOrders] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [s, ins, ord] = await Promise.all([
                    api.get("/dashboard/stats"),
                    api.get("/inspections"),
                    api.get("/orders"),
                ]);
                setStats(s.data);
                setRecent(ins.data.slice(0, 5));
                setMyOrders(ord.data.filter(o => o.estado !== "cerrada").slice(0, 6));
            } catch (e) { console.error(e); }
        })();
    }, []);

    const title = {
        supervisor: "Panel de Supervisión",
        comercial: "Panel Comercial",
        tecnico: `Mi día · ${user?.name?.split(" ")[0] || ""}`,
        cliente: "Mis inspecciones",
    }[user?.role] || "Panel";

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8" data-testid="dashboard-page">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10 border-b border-zinc-200 pb-8">
                <div>
                    <div className="overline mb-2" style={{ color: "#0F766E" }}>Hoy · {new Date().toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" })}</div>
                    <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-none">{title}</h1>
                </div>
                {(user?.role === "comercial" || user?.role === "supervisor") && (
                    <Link to="/orders/new" className="btn-accent" data-testid="dashboard-new-order-btn">Crear orden de servicio →</Link>
                )}
            </div>

            {user?.role === "tecnico" && <TechnicianHseBanner cedula={user.cedula} />}

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-200 mb-10" data-testid="kpi-grid">
                {user?.role === "tecnico" && (
                    <>
                        <Kpi label="Órdenes pendientes" value={stats?.ordenes_pendientes ?? "—"} icon={Calendar} testid="kpi-pending" highlight />
                        <Kpi label="Inspecciones hoy" value={stats?.today ?? "—"} icon={Clock} testid="kpi-today" />
                        <Kpi label="Mis inspecciones" value={stats?.total ?? "—"} icon={ListChecks} testid="kpi-total" />
                        <Kpi label="Hallazgos críticos" value={stats?.hallazgos_criticos ?? "—"} icon={Warning} testid="kpi-critical" />
                    </>
                )}
                {(user?.role === "supervisor" || user?.role === "comercial") && (
                    <>
                        <Kpi label="Clientes activos" value={stats?.clientes ?? "—"} icon={Buildings} testid="kpi-clientes" />
                        <Kpi label="Técnicos activos" value={stats?.tecnicos_activos ?? "—"} icon={UserList} testid="kpi-tecnicos" />
                        <Kpi label="Órdenes en proceso" value={stats?.ordenes_pendientes ?? "—"} icon={Calendar} testid="kpi-pending" highlight />
                        <Kpi label="Inspecciones rojas" value={stats?.rojo ?? "—"} icon={Warning} testid="kpi-rojas" />
                    </>
                )}
                {user?.role === "cliente" && (
                    <>
                        <Kpi label="Inspecciones totales" value={stats?.total ?? "—"} icon={ListChecks} testid="kpi-total" />
                        <Kpi label="Verde" value={stats?.verde ?? 0} icon={ChartLineUp} testid="kpi-verde" />
                        <Kpi label="Amarillo" value={stats?.amarillo ?? 0} icon={Warning} testid="kpi-amarillo" />
                        <Kpi label="Rojo" value={stats?.rojo ?? 0} icon={Warning} testid="kpi-rojo" highlight />
                    </>
                )}
            </div>

            {/* Traffic light */}
            {(stats?.total || 0) > 0 && (
                <div className="grid lg:grid-cols-4 gap-0 border border-zinc-200 mb-10" data-testid="traffic-grid">
                    <TrafficStat color="green" label="Saludable" value={stats?.verde ?? 0} />
                    <TrafficStat color="yellow" label="Atención" value={stats?.amarillo ?? 0} />
                    <TrafficStat color="red" label="Crítico" value={stats?.rojo ?? 0} />
                    <TrafficStat color="gray" label="Sin evaluar" value={stats?.sin_evaluar ?? 0} />
                </div>
            )}

            {/* Pending orders for tecnico */}
            {user?.role === "tecnico" && myOrders.length > 0 && (
                <div className="mb-10">
                    <div className="overline mb-1">Mis próximas visitas</div>
                    <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Órdenes asignadas</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {myOrders.map(o => (
                            <Link key={o.order_id} to={`/orders/${o.order_id}`} className="block border border-zinc-200 hover:border-teal-500 p-4 transition-colors" data-testid={`order-card-${o.order_id}`}>
                                <div className="flex items-start justify-between mb-2">
                                    <div className="font-mono text-[11px] text-zinc-500">{o.order_number}</div>
                                    <EstadoOrden estado={o.estado} />
                                </div>
                                <div className="font-display text-lg font-bold tracking-tight">{o.client?.razon_social || "—"}</div>
                                <div className="text-xs text-zinc-600 mt-0.5">{o.sistema_a_revisar}</div>
                                <div className="text-[11px] text-zinc-500 mt-2 font-mono">📍 {o.direccion?.slice(0, 60)}</div>
                                <div className="text-[11px] text-zinc-500 mt-1">{o.norma_iso}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Recent inspections */}
            <div className="mb-4 flex items-end justify-between">
                <div>
                    <div className="overline mb-1">Actividad reciente</div>
                    <h2 className="font-display text-2xl font-bold tracking-tight">Últimas inspecciones</h2>
                </div>
                <Link to="/inspections" className="text-sm font-semibold hover:underline" style={{ color: "#0F766E" }} data-testid="view-all-link">Ver todas →</Link>
            </div>
            <div className="border border-zinc-200 overflow-x-auto">
                <table className="atia-table" data-testid="recent-table">
                    <thead>
                        <tr><th>ID</th><th>Cliente</th><th>Sistema</th><th>Técnico</th><th>Estado</th><th>Ingreso</th></tr>
                    </thead>
                    <tbody>
                        {recent.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-zinc-500">Sin inspecciones aún.</td></tr>}
                        {recent.map(it => (
                            <tr key={it.inspection_id} className="row-link" onClick={() => (window.location.href = `/inspections/${it.inspection_id}`)} data-testid={`recent-row-${it.inspection_id}`}>
                                <td className="font-mono text-xs">{it.inspection_id}</td>
                                <td className="font-semibold">{it.client?.razon_social || "—"}</td>
                                <td className="text-zinc-600">{it.sistema_revisado}</td>
                                <td className="text-zinc-700">{it.technician_name}</td>
                                <td><TrafficBadge value={it.estado_salud} /></td>
                                <td className="text-zinc-500 font-mono text-xs">{it.fecha_ingreso?.slice(0, 16).replace("T", " ")}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Kpi({ label, value, icon: Icon, testid, highlight }) {
    return (
        <div className={`p-6 border-r border-zinc-200 last:border-r-0 ${highlight ? "bg-zinc-50" : "bg-white"}`} data-testid={testid}>
            <div className="flex items-start justify-between mb-4">
                <span className="overline">{label}</span>
                <Icon size={18} className="text-zinc-400" />
            </div>
            <div className="font-display text-4xl font-black tracking-tighter">{value}</div>
        </div>
    );
}

function TrafficStat({ color, label, value }) {
    const m = { green: "#10B981", yellow: "#F59E0B", red: "#EF4444", gray: "#A1A1AA" }[color];
    return (
        <div className="p-6 border-r border-zinc-200 last:border-r-0 relative">
            <div style={{ background: m, width: 12, height: 12 }} className="absolute top-6 right-6" />
            <div className="overline mb-3">{label}</div>
            <div className="font-display text-4xl font-black tracking-tighter">{value}</div>
        </div>
    );
}
